import Data from "data";

export const canonicalName = (name) => name.replace(/[^\w]/g, "").toLowerCase();

export class Lookup {
  _initItems() {
    const itemByName = (this._itemByName = {});
    const etherealByName = (this._etherealByName = {});
    for (let item of Data.items) {
      if (!item.legacy) {
        if (item.quality === "ethereal") {
          etherealByName[canonicalName(item.name)] = item;
          if (item.defaultName)
            etherealByName[canonicalName(item.defaultName)] = item;
        } else {
          itemByName[canonicalName(item.name)] = item;
          if (item.defaultName)
            itemByName[canonicalName(item.defaultName)] = item;
        }
      }
    }
    for (let item of Data.potions) {
      itemByName[canonicalName(item.name)] = item;
      if (item.defaultName) itemByName[canonicalName(item.defaultName)] = item;
    }
    for (let item of Data.extraItems) {
      itemByName[canonicalName(item.name)] = item;
      if (item.defaultName) itemByName[canonicalName(item.defaultName)] = item;
    }
  }
  get itemByName() {
    if (!this._itemByName) this._initItems();
    return this._itemByName;
  }
  get etherealByName() {
    if (!this._etherealByName) this._initItems();
    return this._etherealByName;
  }

  get setByName() {
    if (!this._setByName) {
      const setByName = (this._setByName = {});
      for (let [id, set] of Object.entries(Data.itemSets)) {
        setByName[canonicalName(set.name)] = id;
        if (set.defaultName) setByName[canonicalName(set.defaultName)] = id;
      }
    }
    return this._setByName;
  }

  get gemByName() {
    if (!this._gemByName) {
      const gemByName = (this._gemByName = {});
      for (let [color, gem] of Object.entries(Data.gemColors)) {
        gem.names.forEach(
          (name, i) => (gemByName[canonicalName(name)] = [i, color])
        );
        if (gem.defaultNames)
          gem.defaultNames.forEach(
            (name, i) => (gemByName[canonicalName(name)] = [i, color])
          );
      }
      for (let [id, gem] of Object.entries(Data.legendaryGems)) {
        gemByName[canonicalName(gem.name)] = [id, 0];
        if (gem.defaultName)
          gemByName[canonicalName(gem.defaultName)] = [id, 0];
      }
    }
    return this._gemByName;
  }

  _initSkills() {
    const skillByName = (this._skillByName = {});
    const skillRuneByName = (this._skillRuneByName = {});
    const runeByName = (this._runeByName = {});
    for (let [charClass, skills] of Object.entries(Data.skills)) {
      for (let [id, skill] of Object.entries(skills)) {
        skillByName[canonicalName(skill.name)] = { charClass, id };
        if (skill.defaultName)
          skillByName[canonicalName(skill.defaultName)] = { charClass, id };
        if (skill.runes) {
          for (let [rune, name] of Object.entries(skill.runes)) {
            skillRuneByName[canonicalName(`${skill.name} ${name}`)] = {
              charClass,
              id,
              rune,
            };
            if (skill.defaultRunes)
              skillRuneByName[
                canonicalName(
                  `${skill.defaultName} ${skill.defaultRunes[rune]}`
                )
              ] = { charClass, id, rune };
            const cname = canonicalName(name);
            if (!runeByName[cname]) runeByName[cname] = [];
            runeByName[cname].push({ charClass, id, rune });
            if (skill.defaultRunes) {
              const oname = canonicalName(skill.defaultRunes[rune]);
              if (!runeByName[oname]) runeByName[oname] = [];
              runeByName[oname].push({ charClass, id, rune });
            }
          }
        }
      }
    }
    for (const [id, stat] of Object.entries(Data.stats)) {
      if (!stat.sanctified) continue;
      const skill = Data.skills[stat.class]?.[stat.skill];
      if (!skill) continue;
      skillByName[canonicalName(`Sanctified ${skill.name}`)] = {
        id,
        sanctified: true,
      };
      if (skill.defaultName)
        skillByName[canonicalName(`Sanctified ${skill.defaultName}`)] = {
          id,
          sanctified: true,
        };
    }
    if (runeByName[canonicalName("Communing with Spirits")]) {
      runeByName[canonicalName("Communicating with Spirits")] =
        runeByName[canonicalName("Communing with Spirits")];
    }
    if (
      skillRuneByName[canonicalName("Wall of Death Communing with Spirits")]
    ) {
      skillRuneByName[
        canonicalName("Wall of Death Communicating with Spirits")
      ] =
        skillRuneByName[canonicalName("Wall of Death Communing with Spirits")];
    }
  }

  get skillByName() {
    if (!this._skillByName) this._initSkills();
    return this._skillByName;
  }
  get skillRuneByName() {
    if (!this._skillRuneByName) this._initSkills();
    return this._skillRuneByName;
  }
  get runeByName() {
    if (!this._runeByName) this._initSkills();
    return this._runeByName;
  }

  get altarByName() {
    if (!this._altarByName) {
      const altarByName = (this._altarByName = {});
      for (const [id, data] of Object.entries(Data.altar)) {
        altarByName[canonicalName(data.name)] = id;
      }
    }
    return this._altarByName;
  }

  get followerByName() {
    if (!this._followerByName) {
      const followerByName = (this._followerByName = {});
      for (let [id, skill] of Object.entries(Data.followerSkills)) {
        followerByName[canonicalName(skill.name)] = id;
        if (skill.defaultName)
          followerByName[canonicalName(skill.defaultName)] = id;
      }
    }
    return this._followerByName;
  }

  get passiveByName() {
    if (!this._passiveByName) {
      const passiveByName = (this._passiveByName = {});
      for (let [charClass, passives] of Object.entries(Data.passives)) {
        for (let [id, passive] of Object.entries(passives)) {
          passiveByName[canonicalName(passive.name)] = { charClass, id };
          if (passive.defaultName)
            passiveByName[canonicalName(passive.defaultName)] = {
              charClass,
              id,
            };
        }
      }
    }
    return this._passiveByName;
  }

  get buffByName() {
    if (!this._buffByName) {
      const buffByName = (this._buffByName = {});
      for (let [id, buff] of Object.entries(Data.shrineBuffs)) {
        buffByName[canonicalName(buff.name)] = { id, alt: false };
        buffByName[canonicalName(buff.altname)] = { id, alt: true };
      }
    }
    return this._buffByName;
  }
}
